import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Grid, MenuItem, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography,
} from "@mui/material";
import { MapContainer, TileLayer, Rectangle, Tooltip as LTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loaderOff, loaderOn, openSnackbar, setPageTitle } from "../redux/actions";
import { modeleTuilesLeaflet, sousDomainesLeaflet, attributionFond } from "../utilities/basemap";
import {
  FiltreTrous, IndicateurSante, LIBELLE_TYPE, Synthese, Trou, couleurScore,
  genererQuete, getIndicateurSante, getSynthese, getTrous, lancerDetection,
} from "../utilities/trouService";

// Taxonomie v2 (mêmes catégories que le back-end).
const CATEGORIES = [
  "Commerce informel", "Restauration", "Services financiers", "Santé", "Éducation",
  "Services de proximité", "Automobile", "Hôtellerie & logement", "Culture & loisirs",
  "Infrastructure & espace public", "Autre",
];

const MAILLE_M = 200;
const DAKAR_BOUNDS: [[number, number], [number, number]] = [[14.63, -17.54], [14.80, -17.30]];

// bbox d'une cellule ~200 m autour de son centre.
function cellule(lat: number, lng: number): [[number, number], [number, number]] {
  const demiLat = MAILLE_M / 111320 / 2;
  const demiLng = demiLat / Math.cos((lat * Math.PI) / 180);
  return [[lat - demiLat, lng - demiLng], [lat + demiLat, lng + demiLng]];
}

const carte = { background: "var(--c-surface)", border: "1.5px solid #1F1E1B", borderRadius: "var(--radius-lg)" };

export default function TrousDonnees() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((s: any) => s.auth?.isLoggedIn);

  const [synthese, setSynthese] = useState<Synthese | null>(null);
  const [indicateur, setIndicateur] = useState<IndicateurSante | null>(null);
  const [trous, setTrous] = useState<Trou[]>([]);
  // La carte montre tous les trous du filtre (jusqu'à 500, plafond du
  // serveur), pas seulement la page du tableau (Z1-32).
  const [trousCarte, setTrousCarte] = useState<Trou[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [taille, setTaille] = useState(25);
  const [chargement, setChargement] = useState(false);
  const [detectionEnCours, setDetectionEnCours] = useState(false);
  const [genereId, setGenereId] = useState<string | null>(null);

  const [fType, setFType] = useState("");
  const [fCategorie, setFCategorie] = useState("");
  const [fScoreMin, setFScoreMin] = useState("");

  useEffect(() => {
    dispatch(setPageTitle("Trous de données"));
    const jwt = sessionStorage.getItem("jwtToken");
    if (!jwt) { navigate("/sign_in"); return; }
    chargerSynthese();
    chargerIndicateur();
    return () => { dispatch(setPageTitle("")); };
  }, []);

  useEffect(() => { chargerTrous(); }, [page, taille, fType, fCategorie, fScoreMin]);

  async function chargerSynthese() {
    try { setSynthese(await getSynthese()); } catch (e) { /* pas de lot encore */ }
  }

  async function chargerIndicateur() {
    try { setIndicateur(await getIndicateurSante()); } catch (e) { /* sources non chargées */ }
  }

  async function chargerTrous() {
    setChargement(true);
    try {
      const params: FiltreTrous = { page, taille };
      if (fType) params.type = fType;
      if (fCategorie) params.categorie = fCategorie;
      if (fScoreMin) params.scoreMin = Number(fScoreMin);
      const [rep, carte] = await Promise.all([getTrous(params), getTrous({ ...params, page: 0, taille: 500 })]);
      setTrous(rep?.contenu ?? []);
      setTotal(rep?.total ?? 0);
      setTrousCarte(carte?.contenu ?? []);
    } catch (e) {
      dispatch(openSnackbar("Impossible de charger les trous de données", "red"));
    } finally {
      setChargement(false);
    }
  }

  async function onDetecter() {
    if (!window.confirm("Relancer la détection ? Le lot de trous actuel sera remplacé.")) return;
    setDetectionEnCours(true);
    dispatch(loaderOn());
    try {
      const s = await lancerDetection();
      setSynthese(s);
      setPage(0);
      await chargerTrous();
      dispatch(openSnackbar("Détection terminée", "green"));
    } catch (e) {
      dispatch(openSnackbar("Échec de la détection", "red"));
    } finally {
      dispatch(loaderOff());
      setDetectionEnCours(false);
    }
  }

  async function onGenererQuete(t: Trou) {
    if (!window.confirm("Générer un brouillon de quête depuis ce trou ?")) return;
    setGenereId(t.id);
    try {
      await genererQuete(t.id);
      dispatch(openSnackbar("Brouillon de quête créé — à relire et publier", "green"));
    } catch (e) {
      dispatch(openSnackbar("Échec de la génération de la quête", "red"));
    } finally {
      setGenereId(null);
    }
  }

  // Cellules géolocalisées à afficher sur la carte.
  const cellules = useMemo(() => trousCarte.filter(t => t.centreLat != null && t.centreLng != null), [trousCarte]);

  return (
    <Box sx={{ p: 3, color: "var(--c-text-primary)" }}>
      {/* En-tête */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700 }}>
            Trous de données
          </Typography>
          <Typography sx={{ color: "var(--c-text-secondary)", fontSize: 13 }}>
            {synthese
              ? `Dernier calcul : ${new Date(synthese.genereLe).toLocaleString("fr-FR")} · ${synthese.nbMaillesPeuplees} mailles peuplées`
              : "Aucun lot calculé — lancez une détection."}
          </Typography>
        </Box>
        <Button variant="contained" onClick={onDetecter} disabled={detectionEnCours}>
          {detectionEnCours ? "Détection…" : "Lancer la détection"}
        </Button>
      </Box>

      {/* Synthèse */}
      {synthese && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { l: "Lieux non supprimés", v: synthese.totalNonSupprime },
            { l: "Sans catégorie", v: synthese.sansCategoryMain },
            { l: "Sans photo", v: synthese.sansPhoto },
            { l: "Lieux du référentiel non confirmés", v: synthese.osmNonConfirmes },
            { l: "Manquants estimés (sous-densité)", v: synthese.volumeEstimeSousDensite },
          ].map((k) => (
            <Grid item xs={6} md={2.4} key={k.l}>
              <Box sx={{ ...carte, p: 2 }}>
                <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>{k.l}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--font-display)" }}>
                  {(k.v ?? 0).toLocaleString("fr-FR")}
                </Typography>
              </Box>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Box sx={{ ...carte, p: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)", mr: 1 }}>Trous par type :</Typography>
              {Object.entries(synthese.nbTrousParType ?? {}).map(([t, n]) => (
                <Chip key={t} size="small" label={`${LIBELLE_TYPE[t] ?? t} : ${n}`}
                  sx={{ background: "var(--c-accent-subtle)", color: "var(--c-text-primary)" }} />
              ))}
              {(!synthese.nbTrousParType || Object.keys(synthese.nbTrousParType).length === 0) && (
                <Typography sx={{ fontSize: 12, color: "var(--c-text-muted)" }}>aucun</Typography>
              )}
            </Box>
          </Grid>
          {/* Synthèse par source de référence */}
          {synthese.sourcesReference && Object.keys(synthese.sourcesReference).length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ ...carte, p: 2 }}>
                <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)", mb: 1 }}>Sources de référence :</Typography>
                {Object.entries(synthese.sourcesReference).map(([nom, s]: [string, any]) => (
                  <Typography key={nom} sx={{ fontSize: 13 }}>
                    <b>{nom}</b> ({s.version}, {s.licence}) : {s.total} structures — {s.confirmes} confirmées,
                    {" "}{s.nonTrouves} non trouvées, {s.sansCoordonnees} sans GPS, {s.absentsDeLaSource} nouvelles par Tribe
                  </Typography>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Indicateur santé (HDX × ANSD) + attribution des sources */}
      {indicateur && (indicateur.populationDakar > 0 || indicateur.nbStructuresHdx > 0) && (
        <Box sx={{ ...carte, p: 2, mb: 3 }}>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "baseline" }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>Santé pour 10 000 hab. (Dakar)</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--font-display)" }}>
                {indicateur.santePour10000 != null ? indicateur.santePour10000.toLocaleString("fr-FR") : "—"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>Structures (HDX)</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{indicateur.nbStructuresHdx.toLocaleString("fr-FR")}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>Population (ANSD)</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{indicateur.populationDakar.toLocaleString("fr-FR")}</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: 11, color: "var(--c-text-muted)", mt: 1 }}>
            Sources : {indicateur.sources.map((s) => `${s.nom} (${s.version}, ${s.licence})`).join(" · ")}
          </Typography>
        </Box>
      )}

      {/* Carte des cellules colorées par score */}
      <Box sx={{ ...carte, overflow: "hidden", mb: 3 }}>
        <MapContainer bounds={DAKAR_BOUNDS} style={{ height: 380, width: "100%" }} scrollWheelZoom>
          <TileLayer url={modeleTuilesLeaflet()} attribution={attributionFond}
            {...({ subdomains: sousDomainesLeaflet() } as any)} />
          {cellules.map((t) => (
            <Rectangle key={t.id} bounds={cellule(t.centreLat!, t.centreLng!)}
              pathOptions={{ color: couleurScore(t.scorePriorite), fillColor: couleurScore(t.scorePriorite), fillOpacity: 0.45, weight: 1 }}>
              <LTooltip direction="top">
                <span style={{ fontWeight: 600 }}>{LIBELLE_TYPE[t.type] ?? t.type}</span><br />
                {t.categoryMain ?? "—"}{t.attribut ? ` · ${t.attribut}` : ""}<br />
                volume {t.volume} · score {t.scorePriorite.toFixed(0)}
              </LTooltip>
            </Rectangle>
          ))}
        </MapContainer>
      </Box>

      {/* Filtres */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <FiltreSelect valeur={fType} onChange={(v) => { setPage(0); setFType(v); }} placeholder="Tous les types"
          options={Object.keys(LIBELLE_TYPE).map((k) => ({ v: k, l: LIBELLE_TYPE[k] }))} />
        <FiltreSelect valeur={fCategorie} onChange={(v) => { setPage(0); setFCategorie(v); }} placeholder="Toutes catégories"
          options={CATEGORIES.map((c) => ({ v: c, l: c }))} />
        <FiltreSelect valeur={fScoreMin} onChange={(v) => { setPage(0); setFScoreMin(v); }} placeholder="Score min."
          options={["25", "40", "55", "70"].map((s) => ({ v: s, l: `≥ ${s}` }))} />
      </Box>

      {/* Liste triée par priorité */}
      <Box sx={{ ...carte, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Type", "Catégorie", "Attribut", "Volume", "Score", "Confiance", "Coût suggéré", ""].map((h) => (
                  <TableCell key={h} sx={{ color: "var(--c-text-secondary)", fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {chargement && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
              )}
              {!chargement && trous.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "var(--c-text-muted)" }}>
                  Aucun trou pour ces filtres.
                </TableCell></TableRow>
              )}
              {!chargement && trous.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell><Chip size="small" label={LIBELLE_TYPE[t.type] ?? t.type}
                    sx={{ background: "var(--c-surface-3)", color: "var(--c-text-primary)" }} /></TableCell>
                  <TableCell>{t.categoryMain ?? "—"}{t.categorySub ? ` · ${t.categorySub}` : ""}</TableCell>
                  <TableCell sx={{ color: "var(--c-text-secondary)" }}>{t.attribut ?? "—"}</TableCell>
                  <TableCell>{t.volume}</TableCell>
                  <TableCell>
                    <span style={{ color: couleurScore(t.scorePriorite), fontWeight: 700 }}>{t.scorePriorite.toFixed(0)}</span>
                  </TableCell>
                  <TableCell sx={{ color: "var(--c-text-secondary)" }}>{Math.round((t.confiance ?? 1) * 100)} %</TableCell>
                  <TableCell sx={{ color: "var(--c-text-secondary)" }}>
                    {t.coutEstimeTips != null ? `${t.coutEstimeTips} Tips` : "—"}
                    {t.coutEstimeFcfa != null ? ` · ${t.coutEstimeFcfa.toLocaleString("fr-FR")} F` : ""}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Créer un brouillon de quête pré-rempli">
                      <span>
                        <Button size="small" variant="outlined" disabled={genereId === t.id}
                          onClick={() => onGenererQuete(t)}>
                          {genereId === t.id ? "…" : "Générer la quête"}
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={taille}
          onRowsPerPageChange={(e) => { setTaille(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Par page"
        />
      </Box>
    </Box>
  );
}

function FiltreSelect({ valeur, onChange, placeholder, options }: {
  valeur: string; onChange: (v: string) => void; placeholder: string; options: { v: string; l: string }[];
}) {
  return (
    <Select value={valeur} displayEmpty size="small" onChange={(e) => onChange(e.target.value)}
      sx={{
        minWidth: 180, background: "var(--c-surface)", color: "var(--c-text-primary)",
        borderRadius: "var(--radius-md)", fontSize: 13,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--c-border)" },
        "& .MuiSvgIcon-root": { color: "var(--c-text-secondary)" },
      }}>
      <MenuItem value="">{placeholder}</MenuItem>
      {options.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
    </Select>
  );
}
